import { ExpandMore } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { MermaidDiagram } from "../MermaidDiagram"
import {
  FORWARD_CHART,
  RECOVERY_CHART,
  ROLLBACK_CHART,
  TOPOLOGY_CHART,
} from "./sstv2FlowCharts"

/**
 * Reference documentation shown on /sa/sstv2-migration: the SST-v2 topology, how
 * a V1→V2 locker migration flows end-to-end, how a rollback reverts it, and what
 * recovers a migration when a step of it fails. Collapsed by default and placed
 * BELOW the live data — it's reference material, not a dashboard.
 *
 * REWRITTEN 2026-07-29 (DEV-193) alongside the diagrams. The previous prose
 * described a second SQS queue, `success_list` chunking and a 300-id cap, none of
 * which exist any more. See `./sstv2FlowCharts` for the full list of what was
 * deleted and the backend files this mirrors; edit the charts there.
 *
 * REVISED 2026-07-31 — **D37 supersedes D5**. The "Forward migration" prose asserted
 * that history is never flipped and that its receipts are counted as processed at
 * ingestion. Both were reversed by `d9047759b` and are now false. The replacement
 * paragraphs carry the two things an operator actually needs from the change: both
 * streams are flipped, and they are walked sequentially and finish independently.
 * (The OLAP recompute debt this also described was removed by LOCKER-501: OLAP v4
 * recomputes the moved slices itself.)
 *
 * ⚠ Prose here and the diagrams in `./sstv2FlowCharts` are ONE documentation surface.
 * They drift apart silently — this file said "never flipped" in a paragraph sitting
 * directly above a chart that said the same thing, so neither contradicted the other
 * and both were wrong. Change them together.
 */

const Sstv2MigrationFlowDoc: FC = () => (
  <Accordion data-ui="UI-SL2W-262" sx={{ mb: 4 }} disableGutters>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
        How migration works — topology, the cutover flip, rollback &amp; recovery
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        We drive migration through the partner's three ordered POST endpoints; each
        step emits SQS events that our consumer folds into one{" "}
        <code>Sstv2Migration</code> job record. Every event of a run carries the
        same <code>sl2_locker_id</code> — that is the <strong>migration ID</strong>{" "}
        this page keys on, and the partner mints a new one per attempt, so a
        re-migration is a separate job rather than more rows on the same one.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        <strong>A migrated reservation is never copied into a new Thing.</strong>{" "}
        The existing V1 Reservation Thing <em>is</em> the reservation, before and
        after; migration rewrites only the ten attributes whose truth actually
        changed, and rewrites all of them at one instant — cutover. Its{" "}
        <code>id</code>, its <code>createdAt</code> and every customer and order
        field are untouched, which is why there is exactly one analytics row per
        physical parcel and why occupancy windows are correct by construction. The
        counters below therefore measure reservations <em>flipped</em>, not
        reservations created.
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Topology &amp; event routing
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        <strong>One queue, one consumer, one durable function.</strong>{" "}
        <code>sl2-sstv2-sqs-queue</code> is the single entry point the partner
        publishes to — it carries <em>both</em> the 14 live
        reservation/locker/door event types and the 9 <code>migration:*</code>{" "}
        types. Ingestion is a pure <strong>accumulator</strong>: for a reservation
        batch it writes the partner's <code>success_list</code> down as immutable
        mapping items (<code>BatchWriteItem</code>, 25 per call) and stops — no
        index lookup, no partner call, no reservation write. That is what keeps a
        30k-reservation migration from ever sitting in front of live locker
        traffic, and it means the only way ingestion can fail is "the write didn't
        land", which SQS redelivery already fixes.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        The accumulated mapping items <em>are</em> the work set. Cutover (
        <code>migration:locker:operational</code>) is what turns that collection
        into work: the consumer activates the V2 locker, supersedes V1, and starts{" "}
        <strong>one durable execution</strong> named{" "}
        <code>flip-&#123;migrationId&#125;</code> — then writes no reservation and
        does not wait. The execution guards, waits for the active stream to close,
        pages the mapping partition, and flips each row in place.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        The consumer <strong>starts</strong> the execution and must never{" "}
        <strong>host</strong> it. Putting <code>withDurableExecution</code> on the
        consumer — or wiring the flip function to a queue via an event source
        mapping — caps the whole flip at 15 minutes and cuts long executions short{" "}
        <em>with no error</em>. The 900-second timeout on the flip function bounds
        one <em>invocation</em>; the execution itself spans many, with a 24-hour
        budget.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <MermaidDiagram chart={TOPOLOGY_CHART} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Forward migration (start → finish)
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        <strong>Both reservation streams are flipped — active and history alike.</strong>{" "}
        A historical reservation differs from an active one only by its{" "}
        <code>status</code>: same page loop, same bulk read, same conditional
        write, same undo record, same rollback. There is no second code path and no
        class of reservation that stays addressed to the retired V1 locker. (This
        reversed an earlier decision on 2026-07-31 — see <strong>D37</strong>. Under
        the old rule a history row could point at a locker that cutover had hidden,
        so it rendered as a reservation belonging to no locker at all, and that was
        considered correct.)
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        The two streams are walked <strong>one after the other — active first,
        then history</strong> — and they finish <em>independently</em>. Active is
        O(occupied doors): tens of rows, usually one page, and it is what a courier
        can be blocked on right now. History is O(every reservation this locker ever
        held) — a real run recorded about 3,000 rows for a single locker, which is
        roughly 13 pages — and it is a background drain that can still be running
        long after the locker is happily serving on V2. So a live V2 locker is{" "}
        <strong>not</strong> evidence that the migration has finished.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        One consequence worth carrying into an incident: the partner's{" "}
        <em>Completed</em> phase means the <em>partner</em> finished sending
        history; our <em>Complete</em> pipeline state means <em>we</em> finished
        writing it. They are different claims and they can be hours apart.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <MermaidDiagram chart={FORWARD_CHART} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Rollback — reverts identity, never state
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        Rollback is a change of <em>addressing scheme</em>, not a rewind of
        history. A flipped row is <strong>restored in place and stays live</strong>:
        its ten identity fields revert from the undo record the flip wrote onto the
        row itself, and nothing else moves. Status, delivery time and the payment
        envelope are kept — if the customer picked the parcel up from the V2 locker
        after cutover, that pickup happened, and neither a partner re-fetch nor a
        snapshot restore may un-happen it.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        Only <strong>adopted</strong> reservations — created from the partner's data
        because SL2 had no V1 ancestor — are archived, because they have no v1 state
        to revert to.{" "}
        <strong>
          "Restored" and "deleted" are different outcomes and must not be read as
          one number:
        </strong>{" "}
        restored rows never left the live table.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <MermaidDiagram chart={ROLLBACK_CHART} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Recovery — the 15-minute tick's three jobs
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
        Each job covers a failure the other two are blind to, so none of them is
        optional. A dead ingest envelope means mapping items that were{" "}
        <strong>never written</strong> — invisible to stalled-flip detection, which
        can only find mappings that exist, so the DLQ re-drive is what recovers it
        and the partner-counter reconcile is its only symptom. A durable execution
        that failed <strong>terminally</strong> gets no automatic retry and cannot
        observe its own death — so the liveness probe restarts it from outside.
      </Typography>
      <MermaidDiagram chart={RECOVERY_CHART} />
    </AccordionDetails>
  </Accordion>
)

export default Sstv2MigrationFlowDoc
